import { useState, useRef, useEffect, forwardRef } from 'react';

const SignatureField = forwardRef(({ field, value, onSign, style }, ref) => {
  const [showModal, setShowModal] = useState(false);

  const handleClick = () => {
    setShowModal(true);
  };

  return (
    <>
      <div
        ref={ref}
        className="pdf-signature-field"
        onClick={handleClick}
        style={{
          position: 'absolute',
          ...style,
          border: style.border || '2px dashed #999',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}
      >
        {value ? (
          <img
            src={value}
            alt="Signature"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain'
            }}
          />
        ) : (
          <span style={{ color: '#999', fontSize: '12px' }}>Click to Sign</span>
        )}
      </div>
      
      {showModal && (
        <SignatureModal
          onSave={(signatureData) => {
            onSign(field.name, signatureData);
            setShowModal(false);
          }}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
});

SignatureField.displayName = 'SignatureField';

// Simple signature modal component
const SignatureModal = ({ onSave, onClose }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, []);

  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    // Scale from CSS size to canvas internal resolution
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoords(e);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoords(e);
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleSave = () => {
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div className="signature-modal-overlay">
      <div className="signature-modal" onClick={(e) => e.stopPropagation()}>
        <div className="signature-modal-header">
          <h3>Sign Here</h3>
          <button onClick={onClose}>×</button>
        </div>
        <div className="signature-pad-container" style={{ backgroundColor: '#fff', borderRadius: 4 }}>
          <canvas
            ref={canvasRef}
            width={600}
            height={200}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            style={{ border: '1px solid #ccc', cursor: 'crosshair', touchAction: 'none', backgroundColor: '#fff', width: '100%', display: 'block' }}
          />
        </div>
        <div className="signature-modal-footer">
          <button onClick={handleClear} className="btn-secondary">Clear</button>
          <button onClick={handleSave} className="btn-primary">Save Signature</button>
        </div>
      </div>
    </div>
  );
};

export default SignatureField;
